import type { JSX } from 'solid-js'
import { For } from 'solid-js'
import type { ComparisonTableContext } from './ComparisonTable'

export enum ComparisonTableRowStyle {
  Default = 'default',
  Strong = 'strong',
  Signed = 'signed',
  Negative = 'negative'
}

export interface ComparisonTableCell {
  readonly content: JSX.Element
  readonly numeric?: number
}

export interface ComparisonTableRowProps<TItem> {
  readonly ctx: ComparisonTableContext<TItem>
  readonly title: JSX.Element
  readonly titleVariant?: 'default' | 'strong'
  readonly style?: ComparisonTableRowStyle
  readonly getValue?: (item: TItem) => JSX.Element
  readonly getNumericValue?: (item: TItem) => number | undefined
  readonly getCell?: (item: TItem) => ComparisonTableCell
  readonly getCellClass?: (args: { monthStr: string; isCurrentMonth: boolean }) => string
  readonly rowClass?: string
}

const ComparisonTableRow = <TItem,>(props: ComparisonTableRowProps<TItem>) => {
  const titleVariant = () => props.titleVariant ?? 'default'
  const style = () => props.style ?? ComparisonTableRowStyle.Default

  const getSignClass = (value: number | undefined): string => {
    if (value === undefined) return '!text-gray-900'
    if (value > 0) return '!text-green-600'
    if (value < 0) return '!text-red-600'
    return '!text-gray-900'
  }

  const cellClass = (monthStr: string): string => {
    if (props.getCellClass) {
      return props.getCellClass({ monthStr, isCurrentMonth: props.ctx.isCurrentMonth(monthStr) })
    }

    const base = props.ctx.getValueCellClass(monthStr)
    if (style() === ComparisonTableRowStyle.Strong) {
      return `${base} font-semibold`
    }

    if (style() === ComparisonTableRowStyle.Negative) {
      return `${base} !text-red-600`
    }

    return base
  }

  return (
    <tr class={props.rowClass}>
      <td
        class={`${props.ctx.getRowLabelCellClass(titleVariant())} sticky left-0 z-10 bg-white border-r border-gray-300`}
        style={{ 'box-shadow': '2px 0 4px -2px rgba(0,0,0,0.1)' }}
      >
        {props.title}
      </td>
      <For each={props.ctx.data()}>
        {(item) => {
          const monthStr = props.ctx.getMonth(item)
          const baseClass = cellClass(monthStr)

          const cell = props.getCell?.(item)
          const content = cell?.content ?? props.getValue?.(item) ?? ''
          const numeric = cell?.numeric ?? props.getNumericValue?.(item)

          if (style() === ComparisonTableRowStyle.Signed) {
            return <td class={`${baseClass} ${getSignClass(numeric)}`}>{content}</td>
          }

          return <td class={baseClass}>{content}</td>
        }}
      </For>
    </tr>
  )
}

export default ComparisonTableRow
